/**
 * The ARMS model's vocabulary, as much of it as the Memory control's engine reads: the nine tiers in the rings' order and
 * colours, an item on a ring, a link between two, and how well a name answers a search.
 *
 * A subset of AI Workbench's webview-ui/src/components/memory/armsModel.ts (see provenance.json). The rest of that file
 * builds ring items from what a repository on the desktop holds (its tools, routines, skills, roadmap, plans and
 * library); the console builds them from the project's memory instead (memoryExplorerModel.ts). What is here is copied
 * unchanged, in the source's order.
 */

/**
 * One ring of the model, outermost first — the order they are drawn in,
 * and the order the work flows: the roadmap says what is to come, the
 * architecture says where in the code it lands, the plans are the work
 * under way to get there, governance what was decided and what threatens
 * it, the Library what was written down; then the four the agent works with.
 * `words` are what else people type for a ring in Find.
 */
export const ARMS_TIERS = [
  { id: 'roadmap', label: 'ROADMAP', title: 'Roadmap', colour: '#ff7a9c' },
  { id: 'architecture', label: 'ARCHITECTURE', title: 'Architecture', colour: '#5ee0d0' },
  { id: 'plans', label: 'PLANS', title: 'Plans', colour: '#5bd07a' },
  // Governance (D158, was Decisions, D118): decisions, the Watchlist's risks, issues and bugs, and lessons learned.
  // The id stays `decisions`, so a ring folded shut or zoomed into before the rename is still that ring.
  { id: 'decisions', label: 'GOVERNANCE', title: 'Governance', colour: '#8f9bff' },
  // The Library (D159): reports, research, guides and ADRs.
  { id: 'library', label: 'LIBRARY', title: 'Library', colour: '#e6c07b' },
  // "Tools": what an agent can reach for (MCP servers, plugins, the workbench's own tools). The id stays `apps`.
  { id: 'apps', label: 'TOOLS', title: 'Tools', colour: '#4da6ff' },
  { id: 'routines', label: 'ROUTINES', title: 'Routines', colour: '#ffb83d' },
  { id: 'memory', label: 'MEMORY', title: 'Memory', colour: '#c77dff' },
  { id: 'skills', label: 'SKILLS', title: 'Skills', colour: '#ff7a2f' },
] as const;

export type ArmsTierId = (typeof ARMS_TIERS)[number]['id'];

/** A link between two ring items: a roadmap row to the area it lands in, to the plan delivering it, a plan to the areas it touches. */
export interface ArmsItemLink {
  source: string;
  target: string;
  relation: string;
}

/**
 * One group above an item in its ring's hierarchy: a project layer, a
 * repository, a repository's own layer, a phase, a kind of skill.
 */
export interface ArmsTrailStep {
  /** Unique among its siblings. */
  id: string;
  label: string;
  /** Sorts siblings before their labels do: a layer keeps the order its document gives it. */
  order?: number;
  /** The group is a kind, so its count says it all: "3 skills", not "Skills · 3 skills". */
  countOnly?: boolean;
}

/** What one of a thing is called and what several are: the counts on the groups above it read "32 areas". */
export type ArmsUnit = readonly [one: string, many: string];

/** One key fact Details shows for an item: "Status · Being mitigated". */
export interface ArmsFact {
  label: string;
  value: string;
}

export interface ArmsItem {
  id: string;
  label: string;
  tier: ArmsTierId;
  /** The key facts Details shows for it, in place of the generic kind and state (Governance and Library items). */
  facts?: ArmsFact[];
  /** Other words Find matches it by: its kind, singular and plural ("risk risks watchlist"). */
  words?: string;
  /** The groups above it in its ring, outermost first (armsHierarchy.ts builds the tree from them). */
  trail?: ArmsTrailStep[];
  /** What it is, for the counts on the groups above it. */
  unit?: ArmsUnit;
  /**
   * The items it sits under instead of its trail: a repository's roadmap
   * item under each project roadmap item delegated to it.
   */
  under?: string[];
  /** What it opens into that is not on the map's own list: a plan's steps. */
  parts?: ArmsItem[];
  /** The grouping a node belongs to — a department in the reference visual. */
  group?: string;
  /** Dimmed when the artefact exists but is switched off. */
  enabled?: boolean;
  detail?: string;
  /**
   * What `detail` holds, so Details can head it honestly and offer to open
   * it only when it is a file: a root-level `AGENTS.md` has no slash in it
   * to guess from, and a tool's description is not somewhere it "lives".
   */
  detailKind?: 'path' | 'schedule' | 'text';
}

/** Does a list of words answer a query at the start of one of them ("risk" in "risk risks watchlist", never "is")? */
export function wordsMatch(words: string | undefined, q: string): boolean {
  return !!words && !!q && ` ${words}`.includes(` ${q}`);
}

/**
 * How well a thing answers a query: lower is better, -1 is no match.
 *
 * The name beats everything else, and the start of the name (or of a word
 * in it) beats the middle — typing "graph" should put `graphify` above
 * `memory-graph`, and both above a skill that merely lives in a folder
 * called graph. Agrees with `matchesQuery` on what matches at all.
 */
export function matchRank(query: string, label: string, group?: string, detail?: string, words?: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const name = label.toLowerCase();
  if (name === q) return 0;
  if (name.startsWith(q)) return 1;
  const at = name.indexOf(q);
  if (at > 0) return /[a-z0-9]/.test(name[at - 1]) ? 3 : 2;
  if ((group ?? '').toLowerCase().includes(q)) return 4;
  if ((detail ?? '').toLowerCase().includes(q)) return 5;
  // Its kind's other words: "risks" finds every risk, "lessons" every lesson (D158).
  if (wordsMatch(words, q)) return 6;
  return -1;
}
